import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Github, Calendar, User, Image as ImageIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { assetUrl, portfolioApi } from '../services/api';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  category: string;
  image_url: string | null;
  slug: string;
  display_order: number;
  is_active: boolean;
  client: string;
  project_date: string;
  live_demo_url: string | null;
  github_url: string | null;
  gallery?: string[];
}

const PortfolioDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [portfolio, setPortfolio] = useState<PortfolioItem | null>(null);
  const [relatedProjects, setRelatedProjects] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadPortfolioBySlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (!isImageModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsImageModalOpen(false);
      if (e.key === 'ArrowRight') goToNextImage();
      if (e.key === 'ArrowLeft') goToPrevImage();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isImageModalOpen]);

  const loadPortfolioBySlug = async (slug: string) => {
    try {
      setLoading(true);
      const response = await portfolioApi.getAll();
      const items = response.data || [];
      const foundItem = items.find((item: any) => item.slug === slug);
      
      if (foundItem) {
        setPortfolio({
          ...foundItem,
          gallery: Array.isArray(foundItem.gallery) ? foundItem.gallery : []
        });
        setCurrentImageIndex(0);
        // Load related projects
        const related = items.filter((item: any) => item.slug !== slug && item.is_active);
        setRelatedProjects(related.slice(0, 3)); // Show max 3 related projects
      } else {
        setError('Portfolio item not found');
      }
    } catch (error) {
      console.error('Error loading portfolio:', error);
      setError('Failed to load portfolio item');
    } finally {
      setLoading(false);
    }
  };

  const allGalleryImages = (() => {
    if (!portfolio) return [];
    const featured = portfolio.image_url ? [portfolio.image_url] : [];
    const gallery = Array.isArray(portfolio.gallery) ? portfolio.gallery : [];
    return [...new Set([...featured, ...gallery].filter(Boolean))] as string[];
  })();

  const normalizeImagePath = (url: string | null | undefined): string => {
    if (!url) return '';
    return String(url).replace(/\\/g, '/').trim();
  };

  const openImageModal = (index: number) => {
    setCurrentImageIndex(index);
    setModalImageUrl(allGalleryImages[index] || null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsImageModalOpen(true);
  };

  const goToNextImage = () => {
    if (!allGalleryImages.length) return;
    setCurrentImageIndex((prev) => {
      const next = (prev + 1) % allGalleryImages.length;
      setModalImageUrl(allGalleryImages[next] || null);
      return next;
    });
  };

  const goToPrevImage = () => {
    if (!allGalleryImages.length) return;
    setCurrentImageIndex((prev) => {
      const next = (prev - 1 + allGalleryImages.length) % allGalleryImages.length;
      setModalImageUrl(allGalleryImages[next] || null);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error || !portfolio) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Project Not Found</h1>
          <p className="text-gray-400 mb-8">{error || 'The portfolio item you are looking for does not exist.'}</p>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
          >
            Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vmin] h-[80vmin] bg-amber-500/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 px-6 pt-32 pb-6 md:px-12 md:pt-36 lg:px-24"
      >
        <div className="flex items-center justify-end gap-4">
          {portfolio.live_demo_url && (
            <a
              href={portfolio.live_demo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
            >
              <ExternalLink size={18} />
              Live Demo
            </a>
          )}
          {portfolio.github_url && (
            <a
              href={portfolio.github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
            >
              <Github size={18} />
              View Code
            </a>
          )}
        </div>
      </motion.header>

      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative px-6 py-16 md:px-12 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Project Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="mb-8">
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="inline-block px-5 py-2.5 bg-amber-500/20 border border-amber-500/50 rounded-full text-amber-400 text-sm font-bold uppercase tracking-wider mb-6 backdrop-blur-md"
                >
                  {portfolio.category}
                </motion.span>
                <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 bg-clip-text text-transparent mb-6 leading-tight">
                  {portfolio.title}
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-10">
                  {portfolio.description}
                </p>
              </div>

              {/* Project Meta */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {portfolio.client && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex items-center gap-4 p-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl hover:border-amber-500/30 transition-all duration-300"
                  >
                    <div className="p-3 bg-amber-500/20 rounded-xl">
                      <User className="text-amber-400" size={24} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm font-medium">Client</p>
                      <p className="text-white font-bold">{portfolio.client}</p>
                    </div>
                  </motion.div>
                )}
                {portfolio.project_date && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center gap-4 p-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl hover:border-amber-500/30 transition-all duration-300"
                  >
                    <div className="p-3 bg-amber-500/20 rounded-xl">
                      <Calendar className="text-amber-400" size={24} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm font-medium">Project Date</p>
                      <p className="text-white font-bold">
                        {new Date(portfolio.project_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>

                          </motion.div>

            {/* Featured Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              {portfolio.image_url ? (
                <div className="relative group">
                  <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6, type: "spring", stiffness: 100 }}
                    className="relative overflow-hidden rounded-3xl shadow-2xl shadow-amber-500/20 border border-white/10"
                  >
                    <img
                      src={assetUrl(portfolio.image_url)}
                      alt={portfolio.title}
                      className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => openImageModal(0)}
                      className="absolute top-4 right-4 p-3 bg-black/50 backdrop-blur-md rounded-full border border-white/20 hover:bg-amber-500 hover:border-amber-500 transition-all duration-300 opacity-0 group-hover:opacity-100"
                    >
                      <ImageIcon size={20} className="text-white group-hover:text-black" />
                    </motion.button>
                  </motion.div>
                </div>
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl flex items-center justify-center border-2 border-gray-700">
                  <ImageIcon size={64} className="text-gray-600" />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Full Gallery Section */}
      {allGalleryImages.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="relative px-6 py-20 md:px-12 lg:px-24"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent mb-4">
                Project Gallery
              </h2>
              <p className="text-gray-400 text-lg">Complete visual walkthrough of this portfolio project</p>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mt-6 rounded-full"></div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allGalleryImages.map((img, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative"
                >
                  <motion.div
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="relative overflow-hidden rounded-3xl border border-white/10 bg-black/40 cursor-pointer"
                    onClick={() => openImageModal(index)}
                  >
                    <img
                      src={assetUrl(normalizeImagePath(img))}
                      alt={`${portfolio.title} gallery ${index + 1}`}
                      className="w-full h-[300px] object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <motion.div
                      initial={{ scale: 0 }}
                      whileHover={{ scale: 1 }}
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    >
                      <div className="p-4 bg-amber-500 rounded-full">
                        <ImageIcon size={24} className="text-black" />
                      </div>
                    </motion.div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
      )}

      {/* Image Modal */}
      <AnimatePresence>
        {isImageModalOpen && allGalleryImages.length > 0 && typeof document !== 'undefined' && createPortal((
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-xl z-[2147483647] flex items-start justify-center pt-6 md:pt-10 px-4"
            onClick={() => setIsImageModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="relative w-full max-w-6xl max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={assetUrl(normalizeImagePath(allGalleryImages[currentImageIndex] || modalImageUrl || allGalleryImages[0]))}
                alt={`${portfolio.title} ${currentImageIndex + 1}`}
                className="max-w-[92vw] max-h-[86vh] w-auto h-auto object-contain rounded-lg"
              />

              {allGalleryImages.length > 1 && (
                <>
                  <button
                    onClick={goToPrevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/55 backdrop-blur-md rounded-full text-white hover:bg-black/75 transition-colors"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={goToNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/55 backdrop-blur-md rounded-full text-white hover:bg-black/75 transition-colors"
                    aria-label="Next image"
                  >
                    <ChevronRight size={24} />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/55 text-xs text-white border border-white/20">
                    {currentImageIndex + 1} / {allGalleryImages.length}
                  </div>
                </>
              )}

              <button
                onClick={() => setIsImageModalOpen(false)}
                className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur-md rounded-full text-white hover:bg-black/70 transition-colors"
              >
                <X size={24} />
              </button>
            </motion.div>
          </motion.div>
        ), document.body)}
      </AnimatePresence>

      {/* Related Projects Section */}
      {relatedProjects.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="relative px-6 py-20 md:px-12 lg:px-24 bg-white/5"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent mb-4">
                Related Projects
              </h2>
              <p className="text-gray-400 text-lg">Explore more of our work</p>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mt-6 rounded-full"></div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative"
                >
                  <Link
                    to={`/portfolio/${project.slug}`}
                    className="block relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl hover:border-amber-500/50 transition-all duration-500 shadow-2xl shadow-black/50"
                  >
                    {/* Project Image */}
                    <div className="aspect-video relative overflow-hidden">
                      {project.image_url ? (
                        <motion.img
                          src={assetUrl(project.image_url)}
                          alt={project.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
                          <ImageIcon size={64} className="text-gray-600" />
                        </div>
                      )}
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-90" />
                      
                      {/* Content */}
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <span className="inline-block px-4 py-2 bg-amber-500/20 border border-amber-500/50 rounded-full text-amber-400 text-xs font-bold uppercase tracking-wider mb-4 backdrop-blur-md">
                          {project.category}
                        </span>
                        <h3 className="text-white text-2xl font-black mb-3 group-hover:text-amber-400 transition-colors">
                          {project.title}
                        </h3>
                        <p className="text-gray-300 text-sm line-clamp-2">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
      )}
    </div>
  );
};

export default PortfolioDetail;
